import { Link, NavLink } from "react-router-dom";
import {
  EyeOff, Eye, Radar, Microscope, BookOpen,
  FlaskConical, KeyRound, Library,
  Menu, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { BrandMark, BrandText } from "./BrandLogo";
import { useSession } from "@/hooks/useSession";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

type NavItem = { to: string; label: string; icon: typeof EyeOff };

const stegoNav: NavItem[] = [
  { to: "/hide", label: "Hide", icon: EyeOff },
  { to: "/reveal", label: "Reveal", icon: Eye },
  { to: "/scan", label: "Scan", icon: Radar },
  { to: "/analyze", label: "Analyze", icon: Microscope },
];

const aiNav: NavItem[] = [
  { to: "/labs", label: "Labs", icon: FlaskConical },
  { to: "/providers", label: "Keys", icon: KeyRound },
  { to: "/labs/wiki", label: "Wiki", icon: Library },
  { to: "/learn", label: "Learn", icon: BookOpen },
];

export function Header() {
  const { isAuthenticated, isSSO, provider } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/50 bg-background/60 backdrop-blur-xl backdrop-saturate-150">
      <div className="container flex h-14 items-center gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 group"
          aria-label="Hideaway home"
        >
          <BrandMark className="h-6 w-6 transition-all duration-300 group-hover:drop-shadow-[0_0_8px_hsl(var(--primary)/0.5)]" />
          <BrandText className="hidden sm:block text-foreground" />
        </Link>

        <nav
          aria-label="Primary"
          className="hidden md:flex flex-1 items-center gap-0.5"
        >
          {stegoNav.map((item) => (
            <DesktopNavLink key={item.to} item={item} />
          ))}
          <div
            className="mx-2 h-4 w-px bg-border/60"
            aria-hidden
            title="Stego Arsenal | AI Labs"
          />
          {aiNav.map((item) => (
            <DesktopNavLink key={item.to} item={item} />
          ))}
        </nav>

        <div className="flex-1 md:hidden" />

        <div className="flex items-center gap-2">
          {isAuthenticated && isSSO && (
            <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] text-primary capitalize rounded-full bg-primary/10 px-2.5 py-0.5 border border-primary/20">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-glow-pulse inline-block" />
              {provider}
            </span>
          )}
          <UserMenu />
          <ThemeToggle />
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="md:hidden rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-border/50 bg-card/80 backdrop-blur-xl"
            aria-label="Mobile navigation"
          >
            <div className="container py-3 space-y-3">
              <MobileNavSection
                title="Stego Arsenal"
                items={stegoNav}
                onNav={() => setMobileOpen(false)}
              />
              <MobileNavSection
                title="AI Labs"
                items={aiNav}
                onNav={() => setMobileOpen(false)}
              />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function DesktopNavLink({ item }: { item: NavItem }) {
  const { to, label, icon: Icon } = item;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "relative inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground rounded-md transition-all duration-200",
          isActive
            ? "text-primary bg-primary/10"
            : "hover:text-foreground hover:bg-muted/50",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
          {isActive && (
            <motion.div
              layoutId="nav-indicator"
              className="absolute inset-0 rounded-md bg-primary/10 border border-primary/20"
              transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
              style={{ zIndex: -1 }}
            />
          )}
        </>
      )}
    </NavLink>
  );
}

function MobileNavSection({
  title,
  items,
  onNav,
}: {
  title: string;
  items: NavItem[];
  onNav: () => void;
}) {
  return (
    <div className="space-y-0.5">
      <div className="px-3 pt-1 pb-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
        {title}
      </div>
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNav}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-all duration-200",
              isActive
                ? "text-primary bg-primary/10 border-l-2 border-primary"
                : "hover:text-foreground hover:bg-muted/50",
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden />
          {label}
        </NavLink>
      ))}
    </div>
  );
}
